import React, { useEffect, useState } from 'react'
import i18n from 'i18next'

import { handleLogout } from '../features/auth/utils/logout'
import { LOCAL_AUTH_STORAGE_KEYS } from '../features/auth/constants'
import { isTeamsApp } from '../constants'
import CHART_ICON from '../../public/assets/mobile/chart_icon.svg'
import SIGNOUT_ICON from '../../public/assets/signout_icon.svg'

import type { MRDrawerItem } from './MRDrawer'
import DesktopAppVersion from './DesktopAppVersion'

type DesktopMRDrawerProps = {
  items?: MRDrawerItem[]
  selectedId: number
  timeEntriesCount?: number | null
  onSelect: (text: string, order: number) => void
  children?: React.ReactNode
  onChatbotToggle?: (enabled: boolean) => void
}

type DesktopMenuButtonProps = {
  icon: string
  selectedIcon?: string
  label: string
  selected?: boolean
  showNotification?: boolean
  onClick: () => void
}

function DesktopMenuButton({
  icon,
  selectedIcon,
  label,
  selected = false,
  showNotification = false,
  onClick,
}: DesktopMenuButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-current={selected ? 'page' : undefined}
      className={`group relative flex h-[54px] w-[54px] shrink-0 items-center justify-center border-0 p-[15px] ${
        selected ? 'bg-[#333]' : 'bg-transparent hover:bg-[rgba(74,68,89,0.08)]'
      }`}
      onClick={onClick}
    >
      <span className="relative flex h-6 w-6 items-center justify-center">
        <img
          alt=""
          className={`h-6 w-6 max-w-none ${selected ? 'brightness-0 invert' : ''}`}
          src={selected ? selectedIcon || icon : icon}
        />
        {showNotification && (
          <span className="absolute -right-px -top-[3px] h-1.5 w-1.5 rounded-full bg-[#865bdf]" />
        )}
      </span>

      <span
        role="tooltip"
        className="pointer-events-none absolute left-[64px] top-1/2 z-[10000] hidden -translate-y-1/2 whitespace-nowrap rounded bg-[#292929] px-2 py-1 text-xs leading-4 tracking-[0.5px] text-white group-hover:block group-focus-visible:block"
      >
        <span className="absolute -left-[5px] top-1/2 -translate-y-1/2 border-y-[5px] border-r-[5px] border-y-transparent border-r-[#292929]" />
        {label}
      </span>
    </button>
  )
}

function DesktopMRDrawer({
  items = [],
  selectedId,
  timeEntriesCount = null,
  onSelect,
  children,
  onChatbotToggle,
}: DesktopMRDrawerProps) {
  const [chatbotEnabled, setChatbotEnabled] = useState(false)
  const defaultItems = items.filter(item => item.type === 'default')
  const toolItems = items.filter(item => item.type === 'tools')

  const getItemLabel = (item: MRDrawerItem) => {
    const label = i18n.t('actions.' + item.text)

    if (
      item.text === 'timeEntries' &&
      typeof timeEntriesCount === 'number' &&
      timeEntriesCount > 0
    ) {
      return `${label} (${timeEntriesCount})`
    }

    return label
  }

  const handleChatbotToggle = () => {
    const nextEnabled = !chatbotEnabled
    setChatbotEnabled(nextEnabled)
    onChatbotToggle?.(nextEnabled)
  }

  const handleSignOut = () => {
    handleLogout()
    if (isTeamsApp) {
      localStorage.setItem(LOCAL_AUTH_STORAGE_KEYS.showBreak, 'true')
      window.location.reload()
      return
    }

    window.location.href = '/'
  }

  useEffect(() => {
    onChatbotToggle?.(chatbotEnabled)
  }, [])

  return (
    <div className="flex h-screen w-full bg-white">
      <nav
        aria-label="Main navigation"
        className="relative flex h-full w-[54px] shrink-0 flex-col justify-between overflow-visible border-r border-[#d9d9d9] bg-white"
      >
        <div className="flex flex-col">
          {defaultItems.map(item => (
            <DesktopMenuButton
              key={item.order}
              icon={item.icon}
              selectedIcon={item.selected_icon}
              label={getItemLabel(item)}
              selected={item.order === selectedId}
              onClick={() => onSelect(item.text, item.order)}
            />
          ))}
          {toolItems.map(item => (
            <DesktopMenuButton
              key={`${item.text}-${item.order}`}
              icon={item.icon}
              selectedIcon={item.selected_icon}
              label={getItemLabel(item)}
              selected={item.order === selectedId}
              onClick={() => onSelect(item.text, item.order)}
            />
          ))}
          <DesktopMenuButton
            icon={CHART_ICON}
            label={chatbotEnabled ? 'Hide Chatbot' : 'Show Chatbot'}
            showNotification={chatbotEnabled}
            onClick={handleChatbotToggle}
          />
        </div>

        <div className="flex flex-col items-center gap-1 pt-1">
          <DesktopAppVersion />
          <DesktopMenuButton
            icon={SIGNOUT_ICON}
            label={i18n.t('actions.signout')}
            onClick={handleSignOut}
          />
        </div>
      </nav>

      <main className="flex min-w-0 flex-1 flex-col overflow-hidden bg-white">
        {children}
      </main>
    </div>
  )
}

export default DesktopMRDrawer
